import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { animate, motion, useScroll, useTransform } from "framer-motion"
import { Collection, Tab, TabList, TabPanel, Tabs } from "react-aria-components"

import { useTranslation } from "@be-miniapp/i18n"
import { useClientSessionSetting } from "@be-miniapp/shared/stores/client-session-setting"
import { ActionBar } from "@be-miniapp/ui/components/ui/action-bar"
import { useScrollRestoration } from "@be-miniapp/ui/hooks/use-scroll-restoration"

import { useFoodSessionData } from "@/store/food-data"

import { ListTransportActivities } from "./list-transport"

export const ActivityTab = memo(() => {
  const selectedKey = useClientSessionSetting((state) => state.activityTab)
  const setSelectedKey = useClientSessionSetting(
    (state) => state.changeActivityTab
  )

  const tabListRef = useRef<HTMLDivElement>(null)
  const tabPanelsRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  const { scrollXProgress } = useScroll({
    container: tabPanelsRef,
  })

  const isFoodEnabled = useFoodSessionData((state) => state.isEnabled)

  // react-aria-components Collection mutates item objects internally
  // (attaches .render, .type, etc.), so items must be fresh mutable objects.
  const tabs = useMemo(
    () =>
      isFoodEnabled
        ? [{ id: "transport" }, { id: "food" }]
        : [{ id: "transport" }],
    [isFoodEnabled]
  )

  const [tabElements, setTabElements] = useState<NodeListOf<Element> | null>(
    null
  )
  useEffect(() => {
    if (tabElements) return
    const found = tabListRef.current?.querySelectorAll("[role=tab]")
    if (found?.length) setTabElements(found)
  }, [tabElements])

  const tabCount = tabElements?.length ?? 0

  const getIndex = useCallback(
    (x: number) => Math.max(0, Math.floor((tabCount - 1) * x)),
    [tabCount]
  )

  const transform = useCallback(
    (x: number, property: "offsetLeft" | "offsetWidth") => {
      if (!tabElements?.length) return 0

      const index = getIndex(x)
      const el = tabElements[index] as HTMLElement
      const nextEl = tabElements[index + 1] as HTMLElement | undefined

      const difference = nextEl
        ? (nextEl[property] as number) - (el[property] as number)
        : el.offsetWidth

      const percent = (tabElements.length - 1) * x - index
      return el[property] + difference * percent || 0.1
    },
    [tabElements, getIndex]
  )

  const x = useTransform(scrollXProgress, (v) => transform(v, "offsetLeft"))
  const width = useTransform(scrollXProgress, (v) =>
    transform(v, "offsetWidth")
  )

  useEffect(() => {
    return scrollXProgress.on("change", (x) => {
      if (animationRef.current || !tabElements?.length) return
      setSelectedKey(tabs[getIndex(x)].id)
    })
  }, [scrollXProgress, getIndex, tabElements, setSelectedKey, tabs])

  const animationRef = useRef<ReturnType<typeof animate> | null>(null)
  const onSelectionChange = useCallback(
    (key: string, duration = 0.6) => {
      setSelectedKey(key)

      if (scrollXProgress.getVelocity() && !animationRef.current) return

      const tabPanel = tabPanelsRef.current
      if (!tabPanel) return

      const index = tabs.findIndex((tab) => tab.id === key)
      if (animationRef.current) animationRef.current.stop()

      animationRef.current = animate(
        tabPanel.scrollLeft,
        tabPanel.scrollWidth * (index / tabs.length),
        {
          type: "spring",
          bounce: 0.2,
          duration,
          onUpdate: (v) => {
            tabPanel.scrollLeft = v
          },
          onPlay: () => {
            tabPanel.style.scrollSnapType = "none"
          },
          onComplete: () => {
            tabPanel.style.scrollSnapType = ""
            animationRef.current = null
          },
        }
      )
    },
    [setSelectedKey, scrollXProgress, tabs]
  )

  useScrollRestoration({
    key: `transport-activities-${selectedKey}`,
    scrollableRef: tabPanelsRef,
  })

  useEffect(() => {
    onSelectionChange(selectedKey, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectionChange, selectedKey])

  return (
    <div className="flex size-full min-h-0 flex-col">
      <ActionBar>{t("home.activities.title")}</ActionBar>
      <div className="flex flex-1 flex-col">
        <Tabs
          className="mx-auto flex size-full flex-col bg-be-neutral-25"
          selectedKey={selectedKey}
          onSelectionChange={onSelectionChange as any}
        >
          <div className="bg-white pb-3 shadow-sm">
            <div className="relative px-4">
              <TabList ref={tabListRef} className="flex space-x-1" items={tabs}>
                {(tab) =>
                  // prettier-ignore
                  <Tab className={({ isSelected }) =>`${isSelected ? "" : "data-[hovered]:text-black/70 data-[pressed]:text-black/60"} cursor-default touch-none px-3 py-2 font-bold text-black text-md text-xs uppercase outline-none transition sm:text-sm`}>
                    {() => <>{t((`home.activities.tabs.${tab.id}`) as any)}</>}
                  </Tab>
                }
              </TabList>
              {/* Selection indicator. */}
              <motion.span
                className="absolute inset-0 z-10 rounded-full bg-white mix-blend-difference"
                style={{ x, width }}
              />
            </div>
          </div>
          <div className="relative w-full flex-1">
            <div className="absolute inset-0 flex">
              <div
                ref={tabPanelsRef}
                className="no-scrollbar flex size-full snap-x snap-mandatory overflow-auto font-light text-black text-sm"
              >
                <Collection items={tabs}>
                  {(tab) => (
                    <TabPanel
                      shouldForceMount
                      className="no-scrollbar w-full shrink-0 snap-start overflow-auto rounded outline-none -outline-offset-2 data-[focus-visible]:outline-black"
                    >
                      {tab.id === "transport" && <ListTransportActivities />}
                      {/* {tab.id === "food" && <ListFoodActivities />} */}
                    </TabPanel>
                  )}
                </Collection>
              </div>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  )
})

ActivityTab.displayName = "ActivityTab"
